import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth, db, USER_PROFILES } from './supabase'
import { toUser, isSuperadmin as isSA } from './permissions'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined) // undefined = loading, null = logged out
  const [profile, setProfile] = useState(null)       // { name, role } from USER_PROFILES

  // Seed the profiles mapping (uid -> agent + role) so Supabase RLS can authorize.
  const seedProfile = (s, p) => {
    if (!s || !p) return
    const u = toUser(p)
    db.upsertProfile(s.user.id, u.name, u.role).catch(() => {}) // best-effort, never blocks login
  }

  useEffect(() => {
    auth.getSession().then(s => {
      const p = s ? (USER_PROFILES[s.user.email] ?? null) : null
      setSession(s)
      setProfile(p)
      seedProfile(s, p)
    })

    const { data: listener } = auth.onAuthStateChange(s => {
      const p = s ? (USER_PROFILES[s.user.email] ?? null) : null
      setSession(s)
      setProfile(p)
      seedProfile(s, p)
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  const signIn = async (email, password) => { await auth.signIn(email, password) }
  const signOut = async () => { await auth.signOut() }

  const user = toUser(profile)              // { name, role: 'superadmin'|'agent' } | null
  const isSuperadmin = isSA(user)
  const userName = user?.name ?? ''
  const role = user?.role ?? null

  return (
    <AuthContext.Provider value={{
      session, profile, user, userName, role, isSuperadmin,
      // backward-compat aliases
      isManager: isSuperadmin,
      canEditCallLog: true,
      canEditPipeline: true,
      signIn, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
